import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import Layout from '../../Layout/Layout';
import { URL } from '../../AxiosUrlGlobal';
import TicketViewModal from './TicketViewModal';
import styles from './SupportTickets.module.css';

function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    fetchTickets();
  }, [filterStatus, filterPriority, search, dateFrom, dateTo, currentPage]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const skip = (currentPage - 1) * limit;

      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterPriority) params.append('priority', filterPriority);
      if (search) params.append('search', search);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      params.append('limit', limit);
      params.append('skip', skip);

      const response = await axios.get(`${URL}admin/support-tickets?${params.toString()}`, {
        headers: { token }
      });

      if (response.data.success) {
        setTickets(response.data.data);
        setTotal(response.data.total);
      } else {
        toast.error('Failed to load tickets');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
    fetchTickets(); // Refresh list after closing modal
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleClearFilters = () => {
    setFilterStatus('');
    setFilterPriority('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      pending: styles.statusPending,
      in_progress: styles.statusInProgress,
      resolved: styles.statusResolved,
      closed: styles.statusClosed
    };

    const statusLabels = {
      pending: 'Pending',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };

    return (
      <span className={`${styles.statusBadge} ${statusClasses[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: styles.priorityLow,
      normal: styles.priorityNormal,
      high: styles.priorityHigh
    };

    return (
      <span className={`${styles.priorityBadge} ${priorityClasses[priority]}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && tickets.length === 0) {
    return (
      <Layout>
        <div className={styles.loading}>Loading support tickets...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>Support Tickets</h1>
        </div>

        {/* Filters Section */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label>Search</label>
              <input
                type="text"
                placeholder="Search by name, mobile, or message..."
                value={search}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>Status</label>
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>Priority</label>
              <select value={filterPriority} onChange={(e) => { setFilterPriority(e.target.value); setCurrentPage(1); }}>
                <option value="">All Priority</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label>From Date</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>To Date</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
              />
            </div>

            <div className={styles.filterGroup}>
              <label>&nbsp;</label>
              <button onClick={handleClearFilters} className={styles.clearButton}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <h3>{total}</h3>
            <p>Total Tickets</p>
          </div>
          <div className={styles.statCard}>
            <h3>{tickets.filter(t => t.status === 'pending').length}</h3>
            <p>Pending (Page)</p>
          </div>
          <div className={styles.statCard}>
            <h3>{tickets.filter(t => t.status === 'in_progress').length}</h3>
            <p>In Progress (Page)</p>
          </div>
          <div className={styles.statCard}>
            <h3>{tickets.filter(t => t.status === 'resolved').length}</h3>
            <p>Resolved (Page)</p>
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          {loading ? (
            <div className={styles.loadingOverlay}>Loading...</div>
          ) : tickets.length === 0 ? (
            <div className={styles.noData}>No support tickets found</div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket._id}>
                    <td>{new Date(ticket.createdAt).toLocaleDateString()}</td>
                    <td>{ticket.name}</td>
                    <td>{ticket.mobile}</td>
                    <td>{getStatusBadge(ticket.status)}</td>
                    <td>{getPriorityBadge(ticket.priority)}</td>
                    <td>
                      <button
                        className={styles.viewButton}
                        onClick={() => handleViewTicket(ticket)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={styles.paginationButton}
            >
              Previous
            </button>

            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages} ({total} total tickets)
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        )}

        {isModalOpen && (
          <TicketViewModal
            ticket={selectedTicket}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </Layout>
  );
}

export default SupportTickets;
